import { ProductTier } from '@sparemin/api-sdk';
import { Link, Tooltip } from '@sparemin/blockhead';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CardWizardStep from 'blocks/OnboardingWizard/components/CardWizard/CardWizardStep';
import { upsellBlock } from 'blocks/OnboardingWizard/utils';
import Button from 'components/Button';
import useContextualUpgrade from 'hooks/useContextualUpgrade';
import { onUnlockThisFeature } from 'redux/modules/mixpanel';
import { pushModal } from 'redux/modules/modal';
import { tierSelector } from 'redux/modules/pricing';
import { Dispatch } from 'redux/types';
import ProFeatureList from './ProFeatureList';

export type UpsellStepProps = {
  onSubmit?: () => void;
};

const UpsellStep: React.FC<UpsellStepProps> = ({ onSubmit }) => {
  const tier = useSelector(tierSelector);

  const dispatch = useDispatch<Dispatch>();

  // this upsell step is only shown to users who load into onboarding in the free
  // tier.  if `tier` is anything other than FREE, then they've upgraded.  shouldn't
  // have to consider tiers like "education"here since they are filtered out of users
  // who see the upsell step
  const hasUpgraded = tier !== ProductTier.FREE;

  const { onUpgrade: upgrade } = useContextualUpgrade({
    // need the value of the upgrade (true/false) and the deafult openModal
    // (replaceModal) doesn't return a  promise with the result
    openModal: pushModal,
    purchaseTier: ProductTier.PRO,
  });

  const handleUpgrade = useCallback(async () => {
    dispatch(onUnlockThisFeature('Onboarding'));

    const didUpgrade = await upgrade();

    if (didUpgrade) {
      onSubmit?.();
    }
  }, [dispatch, onSubmit, upgrade]);

  const handleDecline = useCallback(() => {
    onSubmit?.();
  }, [onSubmit]);

  return (
    <CardWizardStep className={upsellBlock()}>
      <CardWizardStep.H1 className={upsellBlock('title')}>
        Set yourself up for success
        <br />
        with Headliner Pro
      </CardWizardStep.H1>
      <CardWizardStep.H2 className={upsellBlock('subtitle')}>
        Get your podcast ready for growth with these pro-level features:
      </CardWizardStep.H2>
      <ProFeatureList />
      <Tooltip
        isDisabled={!hasUpgraded}
        arrow
        contents="You already have Headliner Pro"
      >
        <Button
          className={upsellBlock('upgrade')}
          fluid
          theme="rainbow"
          onClick={handleUpgrade}
        >
          upgrade to pro
        </Button>
      </Tooltip>
      <Link
        className={upsellBlock('decline')}
        as="button"
        elementType="button"
        typography="heading"
        level={2}
        onPress={handleDecline}
      >
        {!hasUpgraded ? 'Start with the free version' : 'Continue with Pro'}
      </Link>
    </CardWizardStep>
  );
};

export default UpsellStep;
